import type {
  ActivityFilter,
  ActivityItem,
  ChatMessage,
  ChatSummary,
  CompanyDetails,
  CompanyListItem,
  ContactDetails,
  ContactListItem,
  ContactSort,
  CrmCounters,
  CrmMeta,
  DealActivityCounters,
  DealBoardSummary,
  DealDetails,
  DealHistoryItem,
  DealListItem,
  DealSort,
  MessengerStatus,
  Paginated,
  ProductItem,
} from '@erp/shared';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api } from './api';

/** Все запросы CRM начинаются с ['crm'] — realtime-событие перечитывает их разом. */

export const crmMetaQuery = queryOptions({
  queryKey: ['crm', 'meta'],
  queryFn: () => api<CrmMeta>('/crm/meta'),
  staleTime: 60_000,
});

export const crmCountersQuery = queryOptions({
  queryKey: ['crm', 'counters'],
  queryFn: () => api<CrmCounters>('/crm/counters'),
  staleTime: 30_000,
});

export interface DealsFilter {
  scope?: 'mine' | 'all' | 'free';
  state?: 'open' | 'won' | 'lost' | 'all';
  board?: boolean;
  directionId?: string;
  stageId?: string;
  ownerId?: string;
  contactId?: string;
  companyId?: string;
  q?: string;
  createdFrom?: string;
  createdTo?: string;
  closedFrom?: string;
  closedTo?: string;
  amountFrom?: number;
  amountTo?: number;
  source?: string;
  activity?: ActivityFilter;
  sort?: DealSort;
  dir?: 'asc' | 'desc';
  page?: number;
}

export const dealActivityCountersQuery = (f: DealsFilter) =>
  queryOptions({
    queryKey: ['crm', 'deals', 'activity-counters', { ...f, activity: undefined, page: undefined }],
    queryFn: () =>
      api<DealActivityCounters>('/crm/deals/activity-counters', {
        query: { ...f, activity: undefined, page: undefined, board: undefined } as never,
      }),
    placeholderData: keepPreviousData,
  });

export const dealsQuery = (f: DealsFilter) =>
  queryOptions({
    queryKey: ['crm', 'deals', f],
    queryFn: () =>
      api<Paginated<DealListItem>>('/crm/deals', { query: { ...f, pageSize: f.board ? 500 : 50 } as never }),
    placeholderData: keepPreviousData,
  });

export const dealsSummaryQuery = (f: DealsFilter) =>
  queryOptions({
    queryKey: ['crm', 'deals', 'summary', f],
    queryFn: () => api<DealBoardSummary[]>('/crm/deals/summary', { query: f as never }),
  });

export const dealQuery = (id: string) =>
  queryOptions({ queryKey: ['crm', 'deal', id], queryFn: () => api<DealDetails>(`/crm/deals/${id}`) });

export const dealHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['crm', 'deal', id, 'history'],
    queryFn: () => api<DealHistoryItem[]>(`/crm/deals/${id}/history`),
  });

export interface ContactsFilter {
  q?: string;
  mine?: boolean;
  companyId?: string;
  ownerId?: string;
  activity?: ActivityFilter;
  sort?: ContactSort;
  dir?: 'asc' | 'desc';
  page?: number;
}

export const contactsQuery = (f: ContactsFilter) =>
  queryOptions({
    queryKey: ['crm', 'contacts', f],
    queryFn: () => api<Paginated<ContactListItem>>('/crm/contacts', { query: { ...f, pageSize: 50 } }),
    placeholderData: keepPreviousData,
  });

export const contactQuery = (id: string) =>
  queryOptions({
    queryKey: ['crm', 'contact', id],
    queryFn: () => api<ContactDetails>(`/crm/contacts/${id}`),
  });

export const companiesQuery = (f: { q?: string; page?: number }) =>
  queryOptions({
    queryKey: ['crm', 'companies', f],
    queryFn: () => api<Paginated<CompanyListItem>>('/crm/companies', { query: { ...f, pageSize: 50 } }),
    placeholderData: keepPreviousData,
  });

export const companyQuery = (id: string) =>
  queryOptions({
    queryKey: ['crm', 'company', id],
    queryFn: () => api<CompanyDetails>(`/crm/companies/${id}`),
  });

export const activitiesQuery = (f: {
  state?: 'open' | 'done';
  scope?: 'mine' | 'all';
  dealId?: string;
  contactId?: string;
}) =>
  queryOptions({
    queryKey: ['crm', 'activities', f],
    queryFn: () => api<ActivityItem[]>('/crm/activities', { query: f }),
  });

export const productsQuery = (f: { q?: string; includeInactive?: boolean; page?: number }) =>
  queryOptions({
    queryKey: ['crm', 'products', f],
    queryFn: () => api<Paginated<ProductItem>>('/crm/products', { query: { ...f, pageSize: 50 } }),
    placeholderData: keepPreviousData,
  });

export const chatsQuery = (f: { unread?: boolean; q?: string }) =>
  queryOptions({
    queryKey: ['crm', 'chats', f],
    queryFn: () => api<ChatSummary[]>('/crm/chats', { query: f }),
    placeholderData: keepPreviousData,
  });

export const chatQuery = (id: string) =>
  queryOptions({
    queryKey: ['crm', 'chat', id],
    queryFn: () =>
      api<ChatSummary & { openDeal: { id: string; number: number; title: string } | null }>(
        `/crm/chats/${id}`,
      ),
  });

export const chatMessagesQuery = (id: string) =>
  queryOptions({
    queryKey: ['crm', 'chat', id, 'messages'],
    queryFn: () => api<ChatMessage[]>(`/crm/chats/${id}/messages`),
  });

export const messengersQuery = queryOptions({
  queryKey: ['crm', 'messengers'],
  queryFn: () => api<MessengerStatus[]>('/crm/settings/messengers'),
  refetchInterval: 15_000,
});
